import stringApplySearch from "./stringApplySearch";

export type FilterPair<T = string> = [T, string[]];

export const filterList = <T extends object>(
  list: T[] | undefined,
  searchTerm: string,
  predicate: (item: T) => boolean = () => true,
  getSearchableValues: (item: T) => (string | undefined)[] = (item: T) => Object.values(item),
): T[] => {
  if (!list) {
    return [];
  }

  return list.filter(predicate).filter((item) =>
    getSearchableValues(item).some((value) => {
      if (!value || typeof value !== "string") {
        return false;
      }
      return stringApplySearch(value, searchTerm);
    }),
  );
};

// Returns the items in the given list that match both:
// 1. The given search text, and:
// 2. All of the given filter pairs.
export function filterListBySearchAndFilters<T extends object>(
  list: T[],
  searchText: string,
  filters: FilterPair[],
  getSearchableValues: (item: T) => (string | undefined)[],
  getFilterValue: (item: T, filterName: string) => any,
) {
  const matchesAllFilters = (item: T) => {
    return filters.every((filterPair) => matchesFilterPair(item, filterPair, getFilterValue));
  };
  return filterList(list, searchText, matchesAllFilters, getSearchableValues);
}

// Returns true if the given item matches the given filter pair.
// The item matches the filter pair if it matches at least one of the selected filter values
// (or if the filter has no selected values).
function matchesFilterPair<T extends {}>(
  item: T,
  filterPair: FilterPair,
  getFilterValue: (item: T, filterName: string) => any,
) {
  const [filterName, filterValues] = filterPair;
  // If the filter has no values selected, the filter should not be used to filter
  // out items - items should be shown regardless of whether they match this filter.
  if (!filterValues.length) {
    return true;
  }

  const itemValue = getFilterValue(item, filterName);
  if (Array.isArray(itemValue)) {
    // determine intersection of arrays
    const filteredArray = itemValue.filter((value, i) => {
      // handle date values
      if (!isNaN(Date.parse(value))) {
        return (item as Record<string, any>)[filterValues[i]] === value;
      }
      return filterValues.includes(value);
    });
    return !!filteredArray.length;
  } else {
    return filterValues.includes(itemValue);
  }
}

// Returns the number of filters from the given set of filter pairs that are selected,
// plus the number of the given toggles (if any) that are true.
export function getTotalFilterCount(filters: FilterPair[], toggles?: boolean[]): number {
  let count = 0;
  if (filters && filters.length) {
    count = filters.reduce((sum, filterPair: FilterPair) => sum + filterPair[1].length, 0);
  }
  if (toggles && toggles.length) {
    count += toggles.filter((toggle) => toggle).length;
  }
  return count;
}
